/**
 * Hero training for two, in one sitting (CAM-02 + CAM-07, GDS §13, MIN §6/§8, D-076(3)/(7)/(9)).
 *
 * **Nothing about hero training is written a second time.** A cooperative sitting is the shipped
 * seven-step machine run twice — Hero 1 in the left half of the picture, then Hero 2 in the right,
 * in that fixed order because MIN §8 and GDS §10 fix it — followed by GDS §13's cooperative
 * verification: lane separation, both heroes visible, and a shared celebration. This file sequences
 * those and owns nothing else.
 *
 * **Whose body is this is not decided here.** The Slice B′ {@link LaneResolver} is the one owner
 * (D-079(1), recon §B.2's one-owner rule, which is the D-075 lesson made a design input rather than
 * a review finding). Each frame is resolved once; the calibrating hero's flow is handed a frame
 * containing **only that hero's body**, and a lane the resolver will not attribute is handed an
 * empty frame — which the shipped machine already treats as "waiting for a body", resetting whatever
 * was part-way through. That is PS4-3 (D-076(3)) in one rule: the child who is not training may
 * stand at the edge of their own side and be *seen*, without a single reading of theirs reaching the
 * calibrating child's measurements.
 *
 * Why that matters more than it looks: sampling the wrong body for a few frames does not break
 * anything visibly. It produces a profile that is quietly about the other child, and a mission in
 * which one hero's crouch never registers and nobody can say why.
 *
 * **What it deliberately does not do.** It has no camera, no storage, no narration and no mission.
 * Finished profiles are handed out through {@link CooperativeCalibrationOptions.onProfile} for the
 * sitting to hold in its session-only store (GDS §13 "store session-only") — no `thrive.v1.*` key is
 * added, read or proposed. The shared celebration is announced through
 * {@link CooperativeCalibrationOptions.onCelebration}, so the words are spoken by whoever owns the
 * `NarrationService`, not by a machine that would then need to know how to talk.
 *
 * **Non-competitive by construction.** The verification state names no hero. There is no
 * "Hero 1 ready, waiting for Hero 2" — a step is waiting for *the team* or it is done, because the
 * alternative is a comparison between two children standing side by side (ARCH §12, COOP-07, and
 * recon §B.4/D-076(6) extending the invariant to input quality). And no step can ever be *failed*:
 * a hold that is lost is a hold that starts again, warmly.
 */

import {
  COOPERATIVE_CALIBRATION_TUNABLES,
  COOPERATIVE_LANE_SPANS,
  COOPERATIVE_VERIFICATION_LABELS,
  type CooperativeCalibrationTunables,
} from '../../platform/config/cooperative-calibration';
import { CALIBRATION_TUNABLES, type CalibrationTunables } from '../../platform/config/calibration';
import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';
import type { PlayerId } from '../../shared/types/ids';
import type { Viewport } from '../../shared/types/presentation';
import type { GameplayTunables } from '../../shared/types/tunables';
import { HERO_1_PLAYER_ID, HERO_2_PLAYER_ID } from '../keyboard/keyboard-adapter';
import { createHandsDetector } from '../pose/detectors/hands-detector';
import {
  attributedPose,
  laneObservation,
  createLaneResolver,
  LANE_SIDES,
  type LaneResolution,
  type LaneResolver,
  type LaneSide,
} from '../pose/lane-resolver';
import { createObservationContinuity } from '../pose/observation-continuity';
import { mirroredX, torsoCentre } from '../pose/pose-geometry';
import { poseCalibrationFrom } from './calibration-profile';
import type { PoseFrame } from '../pose/pose-types';
import {
  createCalibrationFlow,
  type CalibrationFlow,
  type CalibrationFrameSource,
} from './calibration-flow';
import type { CalibrationProfile } from './calibration-profile';

/** GDS §13's three cooperative-verification steps. */
export type CooperativeVerificationStepId =
  'lane-separation' | 'both-visible' | 'shared-celebration';

/** There is no `failed`. A check that is not satisfied yet is simply still going. */
export type CooperativeVerificationStatus = 'pending' | 'active' | 'passed';

/**
 * What the screen says about the together-step in progress.
 *
 * A code rather than a sentence, like the single-player flow's: the words live in the config layer,
 * and a test can assert what the machine believes without asserting what a child reads. Every one of
 * them is about the *room* or the *team* — none of them can name a child.
 */
export type CooperativeVerificationFeedback =
  | 'WAITING_FOR_BOTH'
  | 'MOVE_APART'
  | 'HARD_TO_SEE'
  | 'HOLD_STILL'
  | 'CELEBRATE_TOGETHER'
  | 'PASSED';

export interface CooperativeVerificationStepState {
  readonly id: CooperativeVerificationStepId;
  /** GDS §13's own words, for a grown-up. */
  readonly label: string;
  readonly status: CooperativeVerificationStatus;
  readonly feedback: CooperativeVerificationFeedback;
  /** How far through this check the family is, 0–1. */
  readonly progress: number;
}

export type CooperativeCalibrationPhase =
  'idle' | 'hero-1' | 'hero-2' | 'verification' | 'complete' | 'cancelled';

export type CooperativeHeroStatus = 'pending' | 'training' | 'trained';

export interface CooperativeHeroState {
  readonly playerId: PlayerId;
  readonly side: LaneSide;
  readonly status: CooperativeHeroStatus;
}

export interface CooperativeCalibrationState {
  readonly status: 'idle' | 'running' | 'complete' | 'cancelled';
  readonly phase: CooperativeCalibrationPhase;
  /** Who the trainer is watching right now, or nobody. */
  readonly activePlayerId: PlayerId | null;
  /** Both heroes, left lane first. Fixed for the sitting (MIN §6). */
  readonly heroes: readonly CooperativeHeroState[];
  readonly verification: readonly CooperativeVerificationStepState[];
  /** The finished profiles, in the order they were measured. */
  readonly profiles: readonly CalibrationProfile[];
}

export interface CooperativeCalibrationOptions {
  readonly viewport: Viewport;
  readonly cameraDeviceId?: string | undefined;
  readonly frames?: CalibrationFrameSource | null | undefined;
  readonly tunables?: GameplayTunables | undefined;
  readonly config?: CalibrationTunables | undefined;
  readonly cooperativeConfig?: CooperativeCalibrationTunables | undefined;
  /** Wall clock, used only for `createdAt`. All timing comes from the frames themselves. */
  readonly now?: (() => number) | undefined;
  /** A hero has finished. The sitting holds the profile; this file does not. */
  readonly onProfile?: ((profile: CalibrationProfile) => void) | undefined;
  /** The two heroes cheered together. Fired once, for narration to speak. */
  readonly onCelebration?: (() => void) | undefined;
  /** Injected in tests; the default is the shipped resolver on the shipped thresholds. */
  readonly resolver?: LaneResolver | undefined;
}

export interface CooperativeCalibration {
  readonly state: CooperativeCalibrationState;
  /** The trainer the screen mounts right now, or null once both heroes are done. */
  readonly activeFlow: CalibrationFlow | null;
  flowFor(playerId: PlayerId): CalibrationFlow;
  start(): void;
  /** Reads one frame for the whole room. The tracker calls this; tests call it directly. */
  observe(frame: PoseFrame): void;
  /** Starts the whole sitting again, from Hero 1. */
  restart(): void;
  cancel(): void;
  attach(): void;
  detach(): void;
  subscribe(listener: () => void): () => void;
}

/** Which hero stands in which half. Fixed, and not an option: MIN §6 and GDS §7 both say so. */
const LANE_PLAYER_IDS: Readonly<Record<LaneSide, PlayerId>> = {
  left: HERO_1_PLAYER_ID,
  right: HERO_2_PLAYER_ID,
};

export function laneSideOf(playerId: PlayerId): LaneSide {
  return playerId === HERO_2_PLAYER_ID ? 'right' : 'left';
}

/** What building a lane's trainer needs, whether it is one of two or the only one running. */
interface LaneFlowOptions {
  readonly viewport: Viewport;
  readonly cameraDeviceId?: string | undefined;
  readonly tunables?: GameplayTunables | undefined;
  readonly config?: CalibrationTunables | undefined;
  readonly now?: (() => number) | undefined;
}

/**
 * One hero's trainer: the **shipped** seven-step machine, told which half of the picture it is
 * measuring (D-079(4)) and given the one play space cooperative camera play has (D-076(4)).
 *
 * Lifted to module scope by Slice F′ so that the sequential two-hero sitting and a single hero's
 * mid-mission retraining build the identical thing. Two call sites constructing "the coop trainer"
 * their own way is exactly the duplicate-fact shape the review loop kept finding (D-075).
 */
function buildLaneFlow(side: LaneSide, options: LaneFlowOptions): CalibrationFlow {
  return createCalibrationFlow({
    playerId: LANE_PLAYER_IDS[side],
    viewport: options.viewport,
    ...(options.cameraDeviceId === undefined ? {} : { cameraDeviceId: options.cameraDeviceId }),
    cameraMode: 'full-motion',
    lane: COOPERATIVE_LANE_SPANS[side],
    ...(options.tunables === undefined ? {} : { tunables: options.tunables }),
    ...(options.config === undefined ? {} : { config: options.config }),
    ...(options.now === undefined ? {} : { now: options.now }),
  });
}

/**
 * One lane's view of a frame: its own body, and nothing else in the room (PS4-3 / D-076(3)).
 *
 * A lane the resolver will not attribute gets an **empty** frame, which the shipped machine already
 * reads as "waiting for a body" — resetting whatever was part-way through. That one rule is the
 * whole of why the tolerated sibling standing at their own lane edge can never be sampled into the
 * calibrating child's measurements.
 */
function laneFrameOf(frame: PoseFrame, resolution: LaneResolution, side: LaneSide): PoseFrame {
  const pose = attributedPose(resolution[side]);
  return { ...frame, poses: pose === null ? [] : [pose] };
}

export interface CooperativeHeroCalibrationOptions extends LaneFlowOptions {
  /** Which hero is being measured. Their lane follows from it and cannot be chosen separately. */
  readonly playerId: PlayerId;
  readonly frames?: CalibrationFrameSource | null | undefined;
  /**
   * The lane view this training attributes through — **required**, and never built here (S3).
   *
   * A mid-mission retraining happens inside a sitting that already has exactly one thing entitled
   * to say whose body a reading is (D-075/D-076(1)). This used to default to a resolver of its own,
   * which put a second identity owner on the live stream: two components reading the same frame and
   * able to disagree, with the trainer's verdict deciding whose measurements got saved. Requiring
   * the view is what makes that unbuildable rather than merely fixed — the caller must say which
   * owner it is reading, and in the app the only answer is
   * {@link CooperativeCameraAdapter.retrainingLaneView}.
   */
  readonly resolver: LaneResolver;
}

/**
 * **One** hero of two, retrained without leaving the mission (Slice F′, D-076(8), GDS §32).
 *
 * D-081(8) left the camera-era overrides unoffered in a cooperative sitting because they would have
 * had to pick a child. This is what they pick *into*: the identical seven-step trainer the
 * cooperative sitting runs, for one lane, with the sibling in frame and tolerated throughout.
 *
 * It satisfies {@link CalibrationFlow} exactly, so the stage's shipped calibration overlay mounts it
 * unchanged — the only difference from a single-player retraining is which half of the picture the
 * portals are thirds of, and whose body reaches the machine. Both of those come from the two helpers
 * above, which the sequential two-hero sitting uses as well: there is one definition of each.
 *
 * It owns no storage, no camera and no mission, exactly as its siblings do.
 */
export function createCooperativeHeroCalibration(
  options: CooperativeHeroCalibrationOptions,
): CalibrationFlow {
  const side = laneSideOf(options.playerId);
  const flow = buildLaneFlow(side, options);
  /**
   * The sitting's own view, handed in. Nothing is constructed here (S3).
   *
   * The local resolver this replaces was built without calibrations, on the reasoning that numbers
   * about to be retired should not refuse a child their own lane. That reasoning was tested and
   * **rejected**: dropping the calibrations is exactly what makes two swapped children
   * indistinguishable, so the trainer measures whoever is standing in the lane — including the
   * sibling. The recorded cost of keeping them is the honest one: a hero the sitting's resolver can
   * no longer recognise is not measured at all here, and takes the shipped per-lane route to their
   * always-live keys (D-083(6), BL-004). Refusing to measure is the PS4-3 rule.
   */
  const resolver = options.resolver;

  let unsubscribe: (() => void) | null = null;

  return {
    get state() {
      return flow.state;
    },
    start: () => flow.start(),
    observe(frame: PoseFrame): void {
      flow.observe(laneFrameOf(frame, resolver.resolve(frame), side));
    },
    skipStep: () => flow.skipStep(),
    confirm: () => flow.confirm(),
    restart: () => flow.restart(),
    cancel(): void {
      flow.cancel();
      resolver.reset();
    },
    attach(): void {
      if (unsubscribe !== null) return;
      // The frames are subscribed **here** rather than by the flow itself: the flow must only ever
      // see this hero's own body, and a flow with its own subscription would see the whole room.
      unsubscribe =
        options.frames?.subscribe((frame) => {
          flow.observe(laneFrameOf(frame, resolver.resolve(frame), side));
        }) ?? null;
    },
    detach(): void {
      unsubscribe?.();
      unsubscribe = null;
    },
    subscribe: (listener: () => void) => flow.subscribe(listener),
  };
}

const VERIFICATION_ORDER: readonly CooperativeVerificationStepId[] = [
  'lane-separation',
  'both-visible',
  'shared-celebration',
];

interface VerificationRuntime {
  status: CooperativeVerificationStatus;
  feedback: CooperativeVerificationFeedback;
  progress: number;
  holdStartedAt: number | null;
  samples: number;
}

function freshVerification(): VerificationRuntime {
  return {
    status: 'pending',
    feedback: 'WAITING_FOR_BOTH',
    progress: 0,
    holdStartedAt: null,
    samples: 0,
  };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function createCooperativeCalibration(
  options: CooperativeCalibrationOptions,
): CooperativeCalibration {
  const tunables = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const config = options.config ?? CALIBRATION_TUNABLES;
  const coopConfig = options.cooperativeConfig ?? COOPERATIVE_CALIBRATION_TUNABLES;
  const now = options.now ?? (() => Date.now());

  const listeners = new Set<() => void>();
  let unsubscribe: (() => void) | null = null;
  let snapshot: CooperativeCalibrationState | null = null;

  const notify = (): void => {
    snapshot = null;
    for (const listener of [...listeners]) listener();
  };

  const resolver =
    options.resolver ??
    createLaneResolver({
      // Neither child has been measured yet. The resolver's scale checks are a veto and a
      // tiebreaker only (D-079(1)), so it works without them and gets sharper as each hero finishes.
      left: {},
      right: {},
      // One continuity bound for the whole sitting: the resolver reads the same tunable this screen
      // and its flows do, so a retune moves both halves of a discontinuity together (S6-7).
      tunables,
    });

  /**
   * One trainer per hero, built exactly as a single-player sitting builds its one — except that it
   * is told which half of the picture it is measuring (D-079(4)) and that the play space is not an
   * option: cooperative camera play is **Full-Motion only** for the MVP (D-076(4)).
   *
   * The construction itself lives in `buildLaneFlow` at module scope since Slice F′, shared with the
   * single-hero mid-mission retraining so that "the cooperative trainer" has one definition.
   */
  function buildFlow(side: LaneSide): CalibrationFlow {
    return buildLaneFlow(side, {
      viewport: options.viewport,
      ...(options.cameraDeviceId === undefined ? {} : { cameraDeviceId: options.cameraDeviceId }),
      tunables,
      config,
      now,
    });
  }

  let flows: Record<LaneSide, CalibrationFlow> = {
    left: buildFlow('left'),
    right: buildFlow('right'),
  };
  let flowSubscriptions: (() => void)[] = [];

  let status: CooperativeCalibrationState['status'] = 'idle';
  let phase: CooperativeCalibrationPhase = 'idle';
  let profiles: CalibrationProfile[] = [];
  let verification = VERIFICATION_ORDER.map(() => freshVerification());
  let verifyIndex = 0;

  /** The celebration's own state. Deliberately not exposed: it is per hero, and nothing may read it. */
  let hands: Record<LaneSide, ReturnType<typeof createHandsDetector>> | null = null;
  /** Whether this sitting is inside a run of frames it was actually shown (S5-9, D-090). */
  const continuity = createObservationContinuity(tunables);
  let canCheer: Record<LaneSide, boolean> = { left: true, right: true };
  /** Whether each hero's celebration is inside a run of frames that lane was shown (S6-3). */
  let celebrationObserved: Record<LaneSide, boolean> = { left: true, right: true };
  let raised: Record<LaneSide, boolean> = { left: false, right: false };
  let cheeredAt: Record<LaneSide, number | null> = { left: null, right: null };
  let celebrated = false;

  const activeSide = (): LaneSide | null =>
    phase === 'hero-1' ? 'left' : phase === 'hero-2' ? 'right' : null;

  function watchFlows(): void {
    for (const release of flowSubscriptions) release();
    flowSubscriptions = LANE_SIDES.map((side) =>
      flows[side].subscribe(() => {
        onFlowChanged(side);
      }),
    );
  }

  /**
   * A hero pressed "yes, that is me".
   *
   * Their profile is taken, handed out, and given to the resolver **for their lane only** — the
   * other lane's is untouched, which is checklist row 15's no-cross-contamination rule at the one
   * place a sitting could break it.
   */
  function onFlowChanged(side: LaneSide): void {
    if (status !== 'running' || activeSide() !== side) {
      notify();
      return;
    }

    const profile = flows[side].state.profile;
    if (flows[side].state.status !== 'complete' || profile === null) {
      notify();
      return;
    }

    profiles = [...profiles, profile];
    resolver.setCalibration(side, poseCalibrationFrom(profile.baselineMeasurements));
    options.onProfile?.(profile);

    if (side === 'left') {
      phase = 'hero-2';
      flows.right.start();
    } else {
      phase = 'verification';
      beginVerification();
    }
    notify();
  }

  function beginVerification(): void {
    verifyIndex = 0;
    verification = VERIFICATION_ORDER.map(() => freshVerification());
    const first = verification[0];
    if (first !== undefined) first.status = 'active';

    // Each hero's own measurements decide what "hands up" means for them — a taller child's arms
    // travel further, and one threshold for two children would ask more of one of them.
    hands = {
      left: createHandsDetector({ calibration: calibrationFor('left'), tunables }),
      right: createHandsDetector({ calibration: calibrationFor('right'), tunables }),
    };
    canCheer = { left: cheerCapable('left'), right: cheerCapable('right') };
    raised = { left: false, right: false };
    cheeredAt = { left: null, right: null };
    celebrationObserved = { left: true, right: true };
    celebrated = false;
  }

  function profileFor(side: LaneSide): CalibrationProfile | null {
    return profiles.find((profile) => profile.playerId === LANE_PLAYER_IDS[side]) ?? null;
  }

  function calibrationFor(side: LaneSide) {
    const profile = profileFor(side);
    return profile === null ? undefined : poseCalibrationFrom(profile.baselineMeasurements);
  }

  /**
   * Whether this hero's cheer can be *measured*.
   *
   * A child who could not raise their hands during training has no threshold that was taken from
   * them, and asking them for a movement nobody measured is how a five-year-old ends up stuck on a
   * celebration. Their part of the cheer is being there — recorded as a bound rather than hidden.
   */
  function cheerCapable(side: LaneSide): boolean {
    return profileFor(side)?.supportedCapabilities.includes('HANDS_RAISED') ?? false;
  }

  /** One lane's view of a frame — `laneFrameOf` at module scope, shared since Slice F′. */
  const laneFrame = laneFrameOf;

  function activeStep(): VerificationRuntime | null {
    return verification[verifyIndex] ?? null;
  }

  function setFeedback(step: VerificationRuntime, feedback: CooperativeVerificationFeedback): void {
    if (step.feedback === feedback) return;
    step.feedback = feedback;
    notify();
  }

  function resetHold(step: VerificationRuntime, feedback: CooperativeVerificationFeedback): void {
    const changed = step.holdStartedAt !== null || step.progress !== 0;
    step.holdStartedAt = null;
    step.samples = 0;
    step.progress = 0;
    setFeedback(step, feedback);
    if (changed) notify();
  }

  /**
   * Everything the verification phase believes about watched time, forgotten (S5-9).
   *
   * The active step's hold, and the celebration in all three of the places it is remembered: the
   * detectors' own posture state (re-baselined, never `reset()` — the loss clock is not this rule's
   * to restart, D-090), this file's copy of it, and the moment each hero was last seen cheering.
   */
  function forgetVerification(): void {
    const step = activeStep();
    if (step !== null) resetHold(step, 'WAITING_FOR_BOTH');
    /**
     * …and the celebration only once there is a celebration to forget (S6-4).
     *
     * The detectors are built when verification begins, two steps before anything uses them, and a
     * detector that has watched nobody is in the one position where a first posture legitimately
     * **is** an onset — the D-072(3) parity contract, attach and raise your hands and get an event.
     * Re-baselining them on a hole during lane-separation therefore invalidates nothing (there is
     * nothing there to invalidate) and costs everything: lane-separation and both-visible never call
     * them, so the silent-baseline flag survives all the way to the celebration and eats the first
     * cheer of the sitting. A family is then stuck on the last step of hero training, cheering, with
     * nothing happening.
     *
     * The hole is real either way; what it invalidates is scoped to the claims that existed when it
     * happened.
     */
    if (!celebrationActive()) return;
    for (const side of LANE_SIDES) forgetCelebration(side);
  }

  /** Whether the shared celebration is the step actually running. */
  function celebrationActive(): boolean {
    return VERIFICATION_ORDER[verifyIndex] === 'shared-celebration';
  }

  /**
   * One hero's celebration, forgotten — in all three places it is remembered (S6-3, S6-4).
   *
   * The detector's own posture (re-baselined, never `reset()`: the loss clock is not this rule's to
   * restart, D-090(2)), this file's copy of it, and the moment that hero was last seen cheering.
   */
  function forgetCelebration(side: LaneSide): void {
    hands?.[side].reacquireSilently?.();
    raised[side] = false;
    cheeredAt[side] = null;
  }

  /** Holds a check for `verifyHoldMs`, timed from the frames themselves rather than a clock. */
  function hold(step: VerificationRuntime, at: number): void {
    if (step.holdStartedAt === null) step.holdStartedAt = at;
    step.samples += 1;
    const held = at - step.holdStartedAt;
    step.progress = round2(Math.max(0, Math.min(1, held / coopConfig.verifyHoldMs)));
    setFeedback(step, 'HOLD_STILL');
    if (held < coopConfig.verifyHoldMs || step.samples < coopConfig.verifyMinSamples) {
      notify();
      return;
    }
    pass(step);
  }

  function pass(step: VerificationRuntime): void {
    step.status = 'passed';
    step.feedback = 'PASSED';
    step.progress = 1;
    verifyIndex += 1;
    const next = activeStep();
    if (next !== undefined && next !== null) {
      next.status = 'active';
    } else {
      phase = 'complete';
      status = 'complete';
    }
    notify();
  }

  function observeVerification(frame: PoseFrame, resolution: LaneResolution): void {
    const step = activeStep();
    if (step === null) return;

    /**
     * Confirmed bodies only, on every row (P-B, S4-3).
     *
     * A frame the resolver is still arguing about is not a child standing where the check says they
     * are, and this screen writes measurements to a five-year-old's profile: a contradicted reading
     * reaching it is the same exposure as one reaching a detector, with a longer half-life.
     */
    const left = attributedPose(resolution.left);
    const right = attributedPose(resolution.right);
    const both = left !== null && right !== null;
    const id = VERIFICATION_ORDER[verifyIndex];

    if (id === 'lane-separation') {
      if (left === null || right === null) {
        resetHold(step, 'WAITING_FOR_BOTH');
        return;
      }
      const leftCentre = torsoCentre(left);
      const rightCentre = torsoCentre(right);
      if (leftCentre === null || rightCentre === null) {
        resetHold(step, 'WAITING_FOR_BOTH');
        return;
      }
      const margin = coopConfig.laneSeparationMargin;
      const separated =
        mirroredX(leftCentre.x) <= 0.5 - margin && mirroredX(rightCentre.x) >= 0.5 + margin;
      if (!separated) {
        resetHold(step, 'MOVE_APART');
        return;
      }
      hold(step, frame.detectedAt);
      return;
    }

    if (id === 'both-visible') {
      if (left === null || right === null) {
        // A blended reading that two bodies collapsed into is *ambiguous*, not visible — and it
        // passes the confidence gate (D-077(2)(c)), which is exactly why identity is asked first.
        resetHold(step, 'WAITING_FOR_BOTH');
        return;
      }
      const dimmest = Math.min(left.coreConfidence, right.coreConfidence);
      if (dimmest < tunables.pose.minCoreConfidence) {
        resetHold(step, 'HARD_TO_SEE');
        return;
      }
      hold(step, frame.detectedAt);
      return;
    }

    observeCelebration(frame, resolution, step, both);
  }

  function observeCelebration(
    frame: PoseFrame,
    resolution: LaneResolution,
    step: VerificationRuntime,
    both: boolean,
  ): void {
    for (const side of LANE_SIDES) {
      /**
       * **The lane-level unobserved edge, at this consumer** (S6-3, D-090(1)).
       *
       * A hole in the frames is not the only way a lane stops being observed, and the sitting-wide
       * timestamp rule above cannot see the other way: the frames keep arriving at an ordinary
       * cadence while the *picture* stops being readable. Two children who walk within
       * `minLaneSeparation` of each other are one indistinguishable situation — both lanes go
       * ambiguous, each is handed the empty frame it would see if nobody were there, and nothing
       * about either child is being watched for as long as it lasts. Thirty-three milliseconds of it
       * is enough: it is far below the 600 ms loss clock and leaves no timestamp gap at all, so two
       * heroes who came out of a crossing with their hands already up produced two rising edges that
       * nobody could attribute, and passed the one step whose whole purpose is to observe two
       * attributable onsets.
       *
       * Edge-triggered and per lane, exactly as the pair's adapters do it, and reading the same
       * predicate they read (`laneObservation`) so the two cannot come to disagree about a crossing.
       */
      const reading = laneObservation(resolution[side]);
      if (reading === 'observed') celebrationObserved[side] = true;
      else if (reading === 'unobserved' && celebrationObserved[side]) {
        celebrationObserved[side] = false;
        forgetCelebration(side);
      }

      const outcome = hands?.[side].observe(laneFrame(frame, resolution, side));
      if (outcome?.kind === 'event') {
        if (outcome.event.type === 'PLAYER_RAISED_HANDS') raised[side] = true;
        if (outcome.event.type === 'PLAYER_LOWERED_HANDS') raised[side] = false;
      } else if (outcome?.kind === 'lost') {
        raised[side] = false;
      }

      const present = attributedPose(resolution[side]) !== null;
      if (present && (raised[side] || !canCheer[side])) cheeredAt[side] = frame.detectedAt;
    }

    const first = cheeredAt.left;
    const second = cheeredAt.right;
    if (
      first !== null &&
      second !== null &&
      Math.abs(first - second) <= coopConfig.celebrationGraceMs
    ) {
      if (!celebrated) {
        celebrated = true;
        options.onCelebration?.();
      }
      pass(step);
      return;
    }

    setFeedback(step, both ? 'CELEBRATE_TOGETHER' : 'WAITING_FOR_BOTH');
  }

  function reset(): void {
    for (const release of flowSubscriptions) release();
    flowSubscriptions = [];
    flows = { left: buildFlow('left'), right: buildFlow('right') };
    resolver.reset();
    resolver.setCalibration('left', undefined);
    resolver.setCalibration('right', undefined);
    profiles = [];
    verification = VERIFICATION_ORDER.map(() => freshVerification());
    verifyIndex = 0;
    hands = null;
    celebrated = false;
    cheeredAt = { left: null, right: null };
    raised = { left: false, right: false };
  }

  function heroStates(): readonly CooperativeHeroState[] {
    return LANE_SIDES.map((side) => {
      const playerId = LANE_PLAYER_IDS[side];
      const trained = profiles.some((profile) => profile.playerId === playerId);
      const heroStatus: CooperativeHeroStatus = trained
        ? 'trained'
        : activeSide() === side
          ? 'training'
          : 'pending';
      return { playerId, side, status: heroStatus };
    });
  }

  const coop: CooperativeCalibration = {
    get state(): CooperativeCalibrationState {
      if (snapshot === null) {
        snapshot = {
          status,
          phase,
          activePlayerId: activeSide() === null ? null : LANE_PLAYER_IDS[activeSide() as LaneSide],
          heroes: heroStates(),
          verification: VERIFICATION_ORDER.map((id, index) => {
            const runtime = verification[index] ?? freshVerification();
            return {
              id,
              label: COOPERATIVE_VERIFICATION_LABELS[id],
              status: runtime.status,
              feedback: runtime.feedback,
              progress: runtime.progress,
            };
          }),
          profiles,
        };
      }
      return snapshot;
    },

    get activeFlow(): CalibrationFlow | null {
      const side = activeSide();
      return side === null ? null : flows[side];
    },

    flowFor(playerId: PlayerId): CalibrationFlow {
      return flows[playerId === HERO_2_PLAYER_ID ? 'right' : 'left'];
    },

    start(): void {
      if (status === 'running') return;
      reset();
      watchFlows();
      status = 'running';
      phase = 'hero-1';
      flows.left.start();
      notify();
    },

    observe(frame: PoseFrame): void {
      if (status !== 'running') return;
      const resolution = resolver.resolve(frame);

      /**
       * The interval before this frame was not observed (S5-9, D-090).
       *
       * The per-hero phase needs nothing here — each `flows[side]` is an ordinary calibration flow
       * and decides this for itself, on the frames it is handed, exactly as a solo sitting does.
       * The **verification** phase holds two claims of its own that a hole can falsify, and both are
       * claims about watched time:
       *
       *  - each step's hold (`hold()` below times `frame.detectedAt − holdStartedAt`, so three
       *    frames and a hole certified a room nobody watched for 800 ms);
       *  - the shared celebration, which is an **onset** — and these are the same
       *    `createHandsDetector`s the mission uses, so a confident return takes the tracking gate's
       *    ready branch, reports no loss, and two children who put their hands up while the picture
       *    was gone are cheering together as far as this step can tell.
       *
       * Both are thrown away and started again. Repeating a step a grown-up is standing over is the
       * cheap failure; certifying a room, or a celebration, that nobody saw is not.
       */
      if (continuity.note(frame.detectedAt) && phase === 'verification') forgetVerification();

      const side = activeSide();
      if (side !== null) {
        flows[side].observe(laneFrame(frame, resolution, side));
        return;
      }
      if (phase === 'verification') observeVerification(frame, resolution);
    },

    restart(): void {
      status = 'idle';
      coop.start();
    },

    cancel(): void {
      status = 'cancelled';
      phase = 'cancelled';
      for (const side of LANE_SIDES) flows[side].cancel();
      reset();
      notify();
    },

    attach(): void {
      if (unsubscribe !== null) return;
      unsubscribe = options.frames?.subscribe((frame) => coop.observe(frame)) ?? null;
    },

    detach(): void {
      unsubscribe?.();
      unsubscribe = null;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };

  return coop;
}
